// @owners { team: patients-team }
import { AltoSpinningLoader, Body, Button, Column, InlineAlert, InputText, LgSpacing, Row } from '@alto/design-system';
import React, { useCallback, useEffect, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { push } from 'react-router-redux';
// eslint-disable-next-line import/no-deprecated
import { fetchIdentityVerificationNeeded, verifyCommsAndUser } from '~shared/actions/verifications';
import { formatDateInput, formatDateStringForAPI } from '~shared/helpers/date';
import { getErrorMessage, getURLParams } from '~shared/helpers/helper';
import { mapControllerToInputProps } from '~shared/helpers/mapFieldToInputProps';
import { replaceApostrophes } from '~shared/helpers/string';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { validateDateOfBirth } from '~shared/validations';
import { Header } from '~web/components/HeaderImage';
import { PageBackground, SingleColumnContentWrapper } from '~web/components/Page';

type Props = {
  readonly params: {
    commsChannel: string;
  };
};

const defaultValues = {
  last_name: '',
  dob: '',
};

export const IdentityVerificationPage = ({ params }: Props) => {
  const { commsChannel } = params;
  const { token } = getURLParams();
  const dispatch = useDispatchShared();
  const isVerifying = useSelectorShared((state) => state.ui.loading.verifyIdentityAndCommsLoading);
  const identityVerificationNeededLoading = useSelectorShared(
    (state) => state.ui.loading.identityVerificationNeededLoading,
  );
  const identityVerificationNeededError = getErrorMessage(
    useSelectorShared((state) => state.ui.errors.identityVerificationNeededError),
  );

  const [isIdentityVerificationNeeded, setIsIdentityVerificationNeeded] = useState<boolean | null | undefined>(null);
  const onIdentityVerificationNeeded = useCallback(async () => {
    // eslint-disable-next-line import/no-deprecated
    const isVerificationNeeded = await dispatch(fetchIdentityVerificationNeeded(token, commsChannel));
    setIsIdentityVerificationNeeded(isVerificationNeeded);
  }, [commsChannel, dispatch, token]);

  useEffect(() => {
    onIdentityVerificationNeeded();
  }, [onIdentityVerificationNeeded]);

  useEffect(() => {
    if (
      !identityVerificationNeededLoading &&
      !identityVerificationNeededError &&
      isIdentityVerificationNeeded === false
    ) {
      dispatch(push(`/communications/verify/${commsChannel}/${token}`));
    }
  }, [
    commsChannel,
    dispatch,
    identityVerificationNeededError,
    identityVerificationNeededLoading,
    isIdentityVerificationNeeded,
    token,
  ]);

  const { control, getValues } = useForm({
    defaultValues,
    mode: 'onBlur',
  });
  const lastNameController = useController({
    name: 'last_name',
    control,
    rules: {
      required: 'Last name cannot be blank',
    },
  });
  const dobController = useController({
    name: 'dob',
    control,
    rules: {
      required: 'Date of birth cannot be blank',
      validate: (value) => validateDateOfBirth(value),
    },
  });

  if (identityVerificationNeededError) {
    return (
      <Column
        center
        flexShrink={1}
        flexGrow={0}
      >
        <LgSpacing />
        <InlineAlert type="error">
          <Body fontFamily="semibold">Failed to Verify</Body>
          <Body>{identityVerificationNeededError}</Body>
        </InlineAlert>
      </Column>
    );
  }

  if (identityVerificationNeededLoading) {
    return (
      <div>
        <Column center>
          <AltoSpinningLoader />
          <Body>Verifying...</Body>
        </Column>
      </div>
    );
  }

  const handleVerifyIdentity = async (values: typeof defaultValues) => {
    const { dob, last_name } = values;
    const dateOfBirth = formatDateStringForAPI(dob);
    const formattedLastName = replaceApostrophes(last_name);
    // eslint-disable-next-line import/no-deprecated
    const success = await dispatch(verifyCommsAndUser(formattedLastName, dateOfBirth, token, commsChannel));
    if (success) {
      dispatch(push(`/communications/verify_identity/${commsChannel}/success?token=${token}`));
    } else {
      dispatch(push(`/communications/verify_identity/${commsChannel}/fail?token=${token}`));
    }
  };

  return (
    <PageBackground white>
      <SingleColumnContentWrapper>
        <Header title="Please confirm your information" />
        <Column gap="MD">
          <InputText
            required
            label="Last name"
            placeholder="Enter last name"
            textContentType="name"
            {...mapControllerToInputProps(lastNameController)}
          />
          <InputText
            required
            label="Date of birth"
            textContentType="birthdate"
            placeholder="MM/DD/YYYY"
            {...mapControllerToInputProps(dobController)}
            onChangeText={(value) => {
              const date = formatDateInput(value);
              dobController.field.onChange(date);
            }}
          />
          <Row
            center
            flexShrink={1}
          >
            <Button
              label="Verify"
              loading={isVerifying}
              loadingLabel="Verifying your identity..."
              disabled={isVerifying}
              onPress={() => {
                handleVerifyIdentity(getValues());
              }}
            />
          </Row>
        </Column>
      </SingleColumnContentWrapper>
    </PageBackground>
  );
};
